import React, { useState } from 'react';
import { QrCode, Clock, Hash, Check, Copy } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import Card from '../ui/Card.jsx';
import SegmentedControl from '../ui/SegmentedControl.jsx';
import QrPaper from '../ui/QrPaper.jsx';

const SessionInfoCards = ({
  session,
  showQR,
  setShowQR,
  isTimerActive,
  elapsedTime,
  formatTime,
  isHost
}) => {
  // Unterstütze sowohl 'code' als auch 'verificationCode' (Backend verwendet verificationCode)
  const sessionCode = session?.code || session?.verificationCode || null;
  const isFocusMode = session?.isFocusMode || false;
  const [copied, setCopied] = useState(false);

  // Rückmeldung direkt am Knopf statt `alert('Code kopiert!')`. Das alte
  // Browser-Fenster hielt die Seite an, musste weggeklickt werden — und in der
  // nativen App (Capacitor-WebView) sieht es aus wie ein Systemfehler.
  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(sessionCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Zwischenablage gesperrt (kein sicherer Kontext, Berechtigung verweigert):
      // Der Code steht gross daneben und kann von Hand abgetippt werden.
    }
  };

  return (
    <div className={`grid grid-cols-1 gap-3 sm:gap-4 ${!isFocusMode && isHost ? 'sm:grid-cols-2' : ''}`}>
      {/* Session Code Card - NUR für Host im Gruppenmodus! */}
      {!isFocusMode && isHost && (
        <Card className="p-4 sm:p-[18px]">
          <h3 className="text-title-s font-semibold text-foreground">Session-Code</h3>
          <p className="mt-0.5 text-meta text-muted-foreground">Teile diesen Code zum Beitreten</p>

          {/* Oktober 2026 („App wie Spot“): ein zweigeteiltes Segment zeigt, was
              gerade zu sehen ist. Vorher bot ein einzelner Knopf den jeweils
              anderen Modus an — man sah nie, wo man ist. */}
          {sessionCode && (
            <SegmentedControl
              size="sm"
              ariaLabel="Code oder QR-Code"
              idPrefix="session-access"
              value={showQR ? 'qr' : 'code'}
              onChange={(v) => setShowQR(v === 'qr')}
              options={[
                { value: 'code', label: 'Code', icon: Hash },
                { value: 'qr', label: 'QR', icon: QrCode },
              ]}
              className="my-3"
            />
          )}

          {!sessionCode ? (
            <div className="mt-3 rounded-xl bg-surface-raised p-3 text-center sm:p-4">
              <p className="text-sm text-muted-foreground">Session-Code wird geladen...</p>
            </div>
          ) : showQR ? (
            // Scannbarkeit (Okt 2026, App-Test): bis 240px, immer auf Weiss mit
            // Ruhezone, Fehlerkorrektur M. Dazu Scan-Ecken wie im Spot und ein
            // diagonaler Aufbau beim ersten Zeigen (ui/QrPaper).
            <div
              className="flex justify-center py-2"
              role="tabpanel"
              id="session-access-panel-qr"
              aria-labelledby="session-access-tab-qr"
            >
              <QrPaper animateKey={sessionCode}>
                <QRCodeSVG
                  value={JSON.stringify({ code: sessionCode })}
                  size={240}
                  level="M"
                  bgColor="#ffffff"
                  fgColor="#111827"
                  className="block h-auto w-full max-w-[240px]"
                  aria-label="QR-Code zum Beitreten"
                  role="img"
                />
              </QrPaper>
            </div>
          ) : (
            <div
              className="rounded-xl bg-surface-raised p-3 text-center sm:p-4"
              role="tabpanel"
              id="session-access-panel-code"
              aria-labelledby="session-access-tab-code"
            >
              <div className="font-mono text-2xl font-bold tracking-wider text-foreground sm:text-3xl">
                {sessionCode}
              </div>
              <button
                onClick={copyCode}
                className="mx-auto mt-2 flex min-h-[40px] items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-primary transition-colors hover:bg-primary-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {copied
                  ? <Check className="h-4 w-4 text-success" aria-hidden="true" />
                  : <Copy className="h-4 w-4" aria-hidden="true" />}
                {copied ? 'Kopiert' : 'Code kopieren'}
              </button>
            </div>
          )}
        </Card>
      )}

      {/* Timer Card */}
      <Card className="p-4 sm:p-[18px]">
        <div className="mb-3">
          <h3 className="text-title-s font-semibold text-foreground">Laufzeit</h3>
          <p className="mt-0.5 text-meta text-muted-foreground">
            {isTimerActive ? 'Aktive Session' : 'Warte auf Start'}
          </p>
        </div>
        <div className="flex items-center justify-center rounded-xl bg-surface-raised p-3 sm:p-4">
          <Clock className="mr-2 h-5 w-5 shrink-0 text-faint sm:mr-3 sm:h-6 sm:w-6" aria-hidden="true" />
          <span className="font-mono text-2xl sm:text-3xl font-bold text-foreground">
            {formatTime(elapsedTime)}
          </span>
        </div>
      </Card>
    </div>
  );
};

export default SessionInfoCards;
